import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Search, Check, Package as PackageIcon } from 'lucide-react';
import { formatIDR } from '../lib/format';

export const PackageSelectorDialog = ({ open, onOpenChange, onPicked }) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    if (!open) return;
    setSelectedId(null); setSearch('');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const params = { is_active: true };
    if (search) params.search = search;
    api.get('/packages', { params }).then(({ data }) => setList(data)).finally(() => setLoading(false));
  }, [open, search]);

  const submit = () => {
    const pkg = list.find(p => p.id === selectedId);
    if (!pkg) return;
    onPicked(pkg);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col bg-card border-border" data-testid="package-selector-dialog">
        <DialogHeader>
          <DialogTitle>Pilih Paket</DialogTitle>
          <DialogDescription>Pilih satu paket aktif untuk dimasukkan ke penawaran/invoice.</DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Cari paket…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="package-selector-search" />
        </div>
        <div className="flex-1 overflow-y-auto rounded-lg border border-border">
          {loading && <div className="p-6 text-center text-muted-foreground">Memuat…</div>}
          {!loading && list.length === 0 && <div className="p-6 text-center text-muted-foreground">Tidak ada paket aktif.</div>}
          <div className="divide-y divide-border">
            {list.map(p => (
              <label key={p.id} className={`flex items-center gap-3 px-3 py-3 cursor-pointer hover:bg-white/4 ${selectedId === p.id ? 'bg-primary/10 border-l-2 border-primary' : ''}`} data-testid={`package-selector-item-${p.id}`}>
                <input type="radio" name="pkg" checked={selectedId === p.id} onChange={() => setSelectedId(p.id)} className="sr-only" />
                <div className="h-9 w-9 rounded-lg bg-primary/10 border border-primary/30 flex items-center justify-center"><PackageIcon className="h-4 w-4 text-primary" /></div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">{p.package_name}</div>
                  <div className="text-xs text-muted-foreground truncate">{p.description}</div>
                  <div className="text-[10px] text-muted-foreground mt-0.5"><span className="px-1.5 py-0.5 rounded-full bg-white/5 border border-white/10">{p.category}</span> · {p.items?.length || 0} item</div>
                </div>
                <div className="text-right">
                  <div className="font-mono-num text-sm font-semibold text-primary">{formatIDR(p.package_price)}</div>
                </div>
              </label>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Batal</Button>
          <Button onClick={submit} disabled={!selectedId} data-testid="package-selector-confirm"><Check className="h-4 w-4 mr-2" />Pilih Paket</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
